import { Injectable, NgModule } from '@angular/core';
import {
  PreloadingStrategy,
  Route,
  RouterModule,
  Routes,
  mapToCanActivate,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { AdminAuthGuard } from './auth/admin-auth.guard';
import { NotFoundComponent } from './errors/404.component';
import { SessionResolve } from '@hub-resolvers/session.resolve';
import { UserResolve } from '@hub-resolvers/user.resolve';
import { BundlesGuard } from './bundles/bundles.guard';
import { AdminPartnersResolve } from './admin/admin-partners/admin-partners.resolver';
import { PartnerType } from './admin/admin-partners/partner-type.enum';
import { phasedMsalGuard } from './auth/phased-guard';
import { RegistrationCompleteComponent } from './auth/registration-complete/registration-complete.component';
import { AccessDeniedComponent } from './auth/access-denied/access-denied.component';
import { PartnerAccessGuard } from 'projects/hub-app/src/app/auth/partner-access.guard';

@Injectable()
export class CustomPreload implements PreloadingStrategy {
  preload(route: Route, fn: () => Observable<any>): Observable<any> {
    return route.data && route.data.preload ? fn() : of(null);
  }
}

const routes: Routes = [
  {
    path: 'login',
    pathMatch: 'full',
    redirectTo: '',
  },
  {
    path: 'upas',
    pathMatch: 'full',
    redirectTo: '',
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'admin/airlines',
  },
  {
    path: '',
    canActivate: [phasedMsalGuard],
    resolve: {
      session: SessionResolve,
      user: UserResolve,
    },
    children: [
      {
        path: 'admin',
        loadChildren: () =>
          import('./admin/admin.module').then((m) => m.AdminModule),
        canActivate: mapToCanActivate([AdminAuthGuard, PartnerAccessGuard]),
      },
      {
        path: 'channels',
        loadChildren: () =>
          import('./channels/channels.module').then((m) => m.ChannelsModule),
      },
      {
        path: 'observer',
        loadChildren: () =>
          import('./observers/observers.module').then((m) => m.ObserversModule),
        data: {
          skipBreadcrumb: true,
        }
      },
      {
        path: 'upas',
        loadChildren: () => import('./upas/upas.module').then((m) => m.UpasModule),
        resolve: {
          partners: AdminPartnersResolve,
        },
        data: {
          partnerType: PartnerType.AIRLINE
        }
      },
      {
        path: 'upa-metrics',
        loadChildren: () =>
          import('./upa-metrics/upa-metrics.module').then(
            (m) => m.UpaMetricsModule
          ),
      },
      {
        path: 'gallery',
        loadChildren: () =>
          import('./gallery/gallery-routing.module').then((m) => m.GalleryRoutingModule),
      },
      {
        path: 'bundles',
        loadChildren: () =>
          import('./bundles/bundles.module').then(
            (m) => m.BundlesModule
          ),
      },
      {
        path: 'flight-search',
        loadChildren: () =>
          import('./general-flight-search/general-flight-search.module').then(
            (m) => m.GeneralFlightSearchModule
          ),
        data: {
          isFlightSearchV2: true,
        },
      },
      {
        path: 'utas',
        loadChildren: () => import('./utas/utas.module').then((m) => m.UtasModule),
      },
      {
        path: 'utasv1',
        loadChildren: () =>
          import('./utas-legacy/utas-legacy.module').then(
            (m) => m.UtasLegacyModule
          ),
      },
      {
        path: 'mediawindow',
        loadChildren: () =>
          import('./mediawindow/media-window.module').then(
            (m) => m.MediaWindowModule
          ),
      },
      {
        path: 'lists',
        loadChildren: () =>
          import('./lists/targeting-lists.module').then(
            (m) => m.TargetingListsModule
          ),
      },
      {
        path: 'seats',
        loadChildren: () =>
          import('./seats/seats.module').then(
            (m) => m.SeatsModule
          ),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./settings/settings.module').then((m) => m.SettingsModule)
      },
      {
        path: 'terms',
        loadChildren: () =>
          import('./terms/terms.module').then((m) => m.TermsModule),
      },
      {
        path: 'dashboard',
        redirectTo: 'channels/usage',
      },
      {
        path: 'reports',
        loadChildren: () =>
          import('./reports/reports.module').then((m) => m.ReportsModule),
      },
    ]
  },
  
  {
    path: 'bundles',
    loadChildren: () =>
      import('./bundles/bundles.module').then(
        (m) => m.BundlesModule
      ),
      canActivate: mapToCanActivate([BundlesGuard, PartnerAccessGuard]),
  },
  {
    path: 'flight-search',
    loadChildren: () =>
      import('./general-flight-search/general-flight-search.module').then(
        (m) => m.GeneralFlightSearchModule
      ),
    canActivate: mapToCanActivate([PartnerAccessGuard]),
    resolve: {
      session: SessionResolve,
      user: UserResolve,
    },
  },
  {
    path: 'utas',
    loadChildren: () => import('./utas/utas.module').then((m) => m.UtasModule),
    canActivate: mapToCanActivate([PartnerAccessGuard]),
    resolve: {
      session: SessionResolve,
      user: UserResolve,
    },
  },
  {
    path: 'utasv1',
    loadChildren: () =>
      import('./utas-legacy/utas-legacy.module').then(
        (m) => m.UtasLegacyModule
      ),
    canActivate: mapToCanActivate([PartnerAccessGuard]),
    resolve: {
      session: SessionResolve,
      user: UserResolve,
    },
  },
  {
    path: 'mediawindow',
    loadChildren: () =>
      import('./mediawindow/media-window.module').then(
        (m) => m.MediaWindowModule
      ),
  },
  {
    path: 'lists',
    loadChildren: () =>
      import('./lists/targeting-lists.module').then(
        (m) => m.TargetingListsModule
      ),
    canActivate: mapToCanActivate([PartnerAccessGuard]),
    resolve: {
      session: SessionResolve,
      user: UserResolve,
    },
  },
  {
    path: 'settings',
    loadChildren: () =>
      import('./settings/settings.module').then((m) => m.SettingsModule),
    canActivate: mapToCanActivate([AdminAuthGuard]),
  },
  {
    path: 'terms',
    loadChildren: () =>
      import('./terms/terms.module').then((m) => m.TermsModule),
  },
  {
    path: 'dashboard',
    redirectTo: 'channels/usage',
  },
  {
    path: 'registration-complete',
    component: RegistrationCompleteComponent,
  },
  { 
    path: 'access-denied',
    component: AccessDeniedComponent,
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: CustomPreload,
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
